import { MouseEvent, TouchEvent, useEffect, useRef } from 'react';
import { FindResult, Level, LevelDefaults } from '../../types/level';
import { DefaultStyles, Styles } from './types';
import { findAll, getSize } from '../../types/levelUtils';
import './canvas-level-render.css';
import CommonUtils from '../../types/commonUtils';
import React from 'react';
import { Panel } from 'primereact/panel';

const DefaultCellSize = 32;

interface Props {
  enabled: boolean;
  levelWidth?: number;
  levelHeight?: number;
  cellSize?: number;
  level: Level;
  onMove: (value: string, ax: number, ay: number) => void;
  options?: Styles;
}

const CanvasLevelRenderComponent = ({
  enabled,
  levelWidth = LevelDefaults.DefaultWidth,
  levelHeight = LevelDefaults.DefaultHeight,
  cellSize = DefaultCellSize,
  level,
  onMove,
  options = DefaultStyles,
}: Props) => {
  const isTouch = CommonUtils.isTouchDevice;
  const canvas = useRef<HTMLCanvasElement>(null);
  const selected = useRef<FindResult | null>(null);

  const onInputUp = () => {
    selected.current = null;
  };

  const onInputDown = (event: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
    const cRect = canvas.current!.getBoundingClientRect();
    const { x, y } = _getXY(event);
    const mx = Math.round(x - cRect.left);
    const my = Math.round(y - cRect.top);

    const found = findAll(level, Math.floor(mx / cellSize), Math.floor(my / cellSize)).filter(
      (f) => !f.obj.metadata.isLocked
    );

    console.log(Math.floor(mx / cellSize) + ' - ' + Math.floor(my / cellSize));

    if (found.length > 0 && enabled) {
      selected.current = found[0];
    }
  };

  const onInputMove = (event: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
    const cRect = canvas.current!.getBoundingClientRect();
    const { x, y } = _getXY(event);
    const mx = Math.round(x - cRect.left);
    const my = Math.round(y - cRect.top);

    if (selected.current && enabled) {
      const ax = Math.floor(mx / cellSize) - selected.current.ax;
      const ay = Math.floor(my / cellSize) - selected.current.ay;
      onMove(selected.current.obj.word, ax, ay);
    }
  };

  useEffect(() => {
    const current = canvas.current;
    if (current) {
      _setupCanvas(current, levelWidth, levelHeight, cellSize);
    }
  });

  useEffect(() => {
    if (canvas.current) {
      _drawLines(canvas.current, levelWidth, levelHeight, cellSize, options);
      _drawObjects(canvas.current, level, options, cellSize);
    }
  });

  return (
    <Panel header="Level">
      <div className="canvas-render">
        {!isTouch && (
          <canvas
            ref={canvas}
            onMouseDown={onInputDown}
            onMouseUp={onInputUp}
            onMouseMove={onInputMove}
          />
        )}
        {isTouch && (
          <canvas
            ref={canvas}
            onTouchStart={onInputDown}
            onTouchEnd={onInputUp}
            onTouchMove={onInputMove}
          />
        )}
      </div>
    </Panel>
  );
};

export default CanvasLevelRenderComponent;

const _getXY = (event: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
  let x = 0;
  let y = 0;

  if ('clientX' in event && 'clientY' in event) {
    x = event.clientX as number;
    y = event.clientY as number;
  } else if ('touches' in event) {
    x = event.touches[0].clientX;
    y = event.touches[0].clientY;
  }

  return { x, y };
};

const _setupCanvas = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  cellSize: number
): void => {
  canvas.width = width * cellSize;
  canvas.height = height * cellSize;
};

const _drawLines = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  cellSize: number,
  options: Styles
): void => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }

  const style = options ?? DefaultStyles;

  ctx.lineWidth = 1;
  ctx.strokeStyle = style.StrokeColor;

  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++) {
      ctx.strokeRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
};

const _drawObjects = (
  canvas: HTMLCanvasElement,
  level: Level,
  options: Styles,
  cellSize: number
): void => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }

  const style = options ?? DefaultStyles;

  ctx.lineWidth = 1;
  ctx.strokeStyle = style.StrokeColor;
  ctx.font = style.Font;

  for (let i = 0; i < level.words.length; i++) {
    const obj = level.words[i];
    const ax = obj.metadata.isVertical ? 0 : 1;
    const ay = obj.metadata.isVertical ? 1 : 0;

    for (let l = 0; l < getSize(obj); l++) {
      ctx.fillStyle = obj.isValid ? style.FillValidColor : style.FillInvalidColor;
      const x = (obj.metadata.x + l * ax) * cellSize;
      const y = (obj.metadata.y + l * ay) * cellSize;
      ctx.fillRect(x, y, cellSize, cellSize);
      ctx.strokeRect(x, y, cellSize, cellSize);

      ctx.fillStyle = style.FontColor;
      ctx.fillText(obj.word[l], x + 10, y + 22, cellSize);
    }
  }
};
